import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase";
import { baixarCsv, montarCsv, numeroParaCsv } from "./csv";
import { faixaDaHora, horaComArtigo } from "./horaNoTexto";

/**
 * As AVALIAÇÕES das ferramentas (000326 — F9 do Plano de Melhorias de Produtos).
 *
 * ZERO regra aqui. Quem decide quem pode avaliar (quem ABRE a ferramenta, pela
 * mesma pergunta que o conteúdo faz), a escala de 1 a 10, o comentário que a
 * nota abaixo de 5 pede, a avaliação única por aluno e a conta da média é o
 * banco. A tela desenha e mostra a frase que ele devolveu.
 *
 * Nenhum `select` cru: a tabela tem `revoke all` (a cicatriz da 000136), e as
 * leituras saem por função.
 */

export type MinhaAvaliacao = {
  utilidade: number;
  facilidade: number;
  satisfacao: number;
  comentario: string | null;
  criada_em: string;
  atualizada_em: string;
};

export type LeituraDaMinha =
  | { ok: true; pode_avaliar: boolean; avaliacao: MinhaAvaliacao | null }
  | { ok: false; detalhe: string };

export type RespostaDaAvaliacao = {
  ok: boolean;
  mudou?: boolean;
  motivo?: string;
  detalhe?: string;
  avaliacao?: MinhaAvaliacao;
};

/** As três perguntas, na ordem do formulário da Lovable — as mesmas, de propósito. */
export const PERGUNTAS = [
  { campo: "utilidade", texto: "De 1 a 10, o quanto esta ferramenta está sendo útil para você?" },
  { campo: "facilidade", texto: "De 1 a 10, o quanto a ferramenta é fácil de usar?" },
  { campo: "satisfacao", texto: "De 1 a 10, o quanto você está satisfeito com esta ferramenta?" },
] as const;

export type CampoDaNota = (typeof PERGUNTAS)[number]["campo"];

export async function lerMinhaAvaliacao(card: string): Promise<LeituraDaMinha> {
  const { data, error } = await supabase.rpc("minha_avaliacao_da_ferramenta", { p_card: card });
  if (error) return { ok: false, detalhe: "não foi possível ler a sua avaliação — tente de novo em instantes." };
  const r = data as { ok: boolean; pode_avaliar?: boolean; avaliacao?: MinhaAvaliacao | null; detalhe?: string };
  if (!r?.ok) return { ok: false, detalhe: r?.detalhe ?? "não foi possível ler a sua avaliação." };
  return { ok: true, pode_avaliar: Boolean(r.pode_avaliar), avaliacao: r.avaliacao ?? null };
}

export async function avaliarFerramenta(
  card: string,
  notas: Record<CampoDaNota, number | null>,
  comentario: string,
): Promise<RespostaDaAvaliacao> {
  const { data, error } = await supabase.rpc("avaliar_ferramenta", {
    p_card: card,
    p_utilidade: notas.utilidade,
    p_facilidade: notas.facilidade,
    p_satisfacao: notas.satisfacao,
    p_comentario: comentario,
  });
  // A frase de erro de rede é a única escrita aqui: a do banco não chegou.
  if (error) return { ok: false, detalhe: "não foi possível falar com o servidor — a avaliação não foi gravada." };
  return (data as RespostaDaAvaliacao) ?? { ok: false, detalhe: "o servidor não respondeu." };
}

// ═══════════════════════════════════════════════════════════════════════════
//  O painel das avaliações (000327) e as suas quatro sub-abas (000328)
// ═══════════════════════════════════════════════════════════════════════════
//
// Pedido de Victor em 29/09: *"Eu quero que essa aba de avaliações tenha as
// mesmas abas da central de feedbacks"* — Visão geral, Feedbacks, Ferramentas e
// Comparar.
//
// ZERO conta aqui também. Qual dia é "hoje" em São Paulo, onde começa e acaba
// cada janela, qual é o período anterior, os baldes do eixo do tempo, as
// médias, as faixas CONTADAS, o NPS adaptado, a variação e quem entra em
// atenção vêm prontos do BANCO. O que mora aqui é tipo, chamada, rótulo e
// formato — e as FAIXAS para PINTAR (ver `FAIXAS`).

/** A resposta de recusa de toda leitura do painel: o banco diz o porquê. */
export type Falha = { ok: false; motivo?: string; detalhe: string };

// ── O período ───────────────────────────────────────────────────────────────

/**
 * O período, como a URL o guarda (`?periodo=`). As mesmas seis escolhas do
 * painel antigo (`feedbacks-cf`): Hoje, 7, 30 e 90 dias, Todos e Personalizado.
 * Os dias são de CALENDÁRIO em São Paulo — "30 dias" é hoje e os 29 dias
 * anteriores, de meia-noite a meia-noite —, e quem resolve isso é o banco
 * (`av_periodo`, 000328): o relógio do navegador pode estar em qualquer fuso.
 */
export type ChaveDoPeriodo = "hoje" | "7" | "30" | "90" | "tudo" | "personalizado";

export const PERIODOS: { chave: ChaveDoPeriodo; rotulo: string }[] = [
  { chave: "hoje", rotulo: "Hoje" },
  { chave: "7", rotulo: "7 dias" },
  { chave: "30", rotulo: "30 dias" },
  { chave: "90", rotulo: "90 dias" },
  { chave: "tudo", rotulo: "Todos" },
  { chave: "personalizado", rotulo: "Personalizado" },
];

/**
 * 30 dias, como o painel antigo. Com "Todos" como padrão (o da 000327), a
 * Visão geral abriria SEM comparação — "Todos" não tem período anterior —, e a
 * variação é o que ela tem de mais útil.
 */
export const PERIODO_PADRAO: ChaveDoPeriodo = "30";

/** O que a URL pede. As datas só existem no personalizado. */
export type PedidoDePeriodo = { chave: ChaveDoPeriodo; de: string | null; ate: string | null };

export type Granularidade = "hora" | "dia" | "semana" | "mes";

/**
 * Como o período é comparado com o anterior (000334). Quem decide é o banco,
 * pelo HORIZONTE — a avaliação de aluno mais recente que o hub tem, nunca
 * depois de agora:
 *   · 'completa': o período já acabou, e o anterior é inteiro;
 *   · 'mesmas_horas': o horizonte está dentro do período, e o anterior vai até
 *     o instante equivalente (ontem até a mesma hora de hoje). É o caso de todo
 *     período que termina hoje: as avaliações do app antigo chegam por
 *     importação, e o dia de hoje só vai até a última;
 *   · 'sem_anterior': «Todos», que não tem com o que comparar;
 *   · 'sem_dado_ainda': o hub ainda não tem avaliação nenhuma do período (por
 *     exemplo, «Hoje» antes da importação do dia). Não há anterior, e a tela diz
 *     por quê — nunca "sem período anterior", que é a frase de «Todos».
 */
export type ComparacaoDoPeriodo = "completa" | "mesmas_horas" | "sem_anterior" | "sem_dado_ainda";

/**
 * O período que o BANCO resolveu. `de` e `ate` são dias de São Paulo
 * (AAAA-MM-DD), os dois inclusive. `anterior` é a janela de mesma duração logo
 * antes — nula em "Todos", que não tem com o que comparar, e em
 * 'sem_dado_ainda'. A granularidade do eixo do tempo sai do tamanho: 1 dia é
 * por hora, até 31 por dia, até 190 por semana (começando na segunda), e acima
 * disso por mês. `horizonte` é até quando o hub sabe (um instante, nulo sem
 * avaliação nenhuma), e `anterior.comparado_ate`, o instante equivalente no
 * anterior — só nas mesmas horas.
 */
export type Periodo = {
  chave: ChaveDoPeriodo;
  de: string;
  ate: string;
  dias: number;
  granularidade: Granularidade;
  comparacao: ComparacaoDoPeriodo;
  horizonte: string | null;
  anterior: { de: string; ate: string; comparado_ate: string | null } | null;
};

const CHAVES = new Set<string>(PERIODOS.map((p) => p.chave));
const DIA_ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A leitura da URL. Chave desconhecida volta ao padrão; data fora do formato vira nula (e o banco recusa, dizendo o porquê). */
export function pedidoDaBusca(busca: URLSearchParams): PedidoDePeriodo {
  const v = busca.get("periodo");
  const chave = (v && CHAVES.has(v) ? v : PERIODO_PADRAO) as ChaveDoPeriodo;
  if (chave !== "personalizado") return { chave, de: null, ate: null };
  const de = busca.get("de");
  const ate = busca.get("ate");
  return { chave, de: de && DIA_ISO.test(de) ? de : null, ate: ate && DIA_ISO.test(ate) ? ate : null };
}

/**
 * Escreve o período numa busca NOVA (a de entrada não é tocada). O padrão não
 * vai para a URL, e as datas só vão no personalizado — senão um link de "90
 * dias" carregaria duas datas mortas que alguém leria como filtro.
 */
export function buscaComPeriodo(busca: URLSearchParams, p: PedidoDePeriodo): URLSearchParams {
  const nova = new URLSearchParams(busca);
  nova.delete("periodo");
  nova.delete("de");
  nova.delete("ate");
  if (p.chave !== PERIODO_PADRAO) nova.set("periodo", p.chave);
  if (p.chave === "personalizado") {
    if (p.de) nova.set("de", p.de);
    if (p.ate) nova.set("ate", p.ate);
  }
  return nova;
}

/** Só o período, para os links que atravessam as sub-abas ("" no padrão). */
export function buscaSoDoPeriodo(p: PedidoDePeriodo): string {
  return buscaComPeriodo(new URLSearchParams(), p).toString();
}

/** Uma chave estável do PEDIDO — para dependência de efeito e para comparar com a resposta. */
export function chaveDoPedido(p: PedidoDePeriodo): string {
  return p.chave === "personalizado" ? `personalizado:${p.de ?? ""}:${p.ate ?? ""}` : p.chave;
}

/**
 * A mesma chave, a partir do que o BANCO respondeu. É ela que cada sub-aba
 * DECLARA (`data-periodo-dos-dados`): entre o clique e a resposta, a tela do
 * período antigo continua de pé, e quem espera "pronto" sem olhar o período lê
 * os números errados (a cicatriz da busca da Central, 14/09).
 */
export function chaveDoPeriodo(p: Periodo): string {
  return p.chave === "personalizado" ? `personalizado:${p.de}:${p.ate}` : p.chave;
}

function parametrosDoPeriodo(p: PedidoDePeriodo) {
  return {
    p_periodo: p.chave,
    p_de: p.chave === "personalizado" ? p.de : null,
    p_ate: p.chave === "personalizado" ? p.ate : null,
  };
}

// ── Os rótulos (apresentação: a data vem pronta, aqui ela só é escrita) ─────

const MES_CURTO = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const MES_LONGO = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const DIA_DA_SEMANA = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];

/**
 * "AAAA-MM-DD" → "dd/mm/aaaa", LENDO a string: `new Date("2026-09-29")` é
 * meia-noite UTC, que em São Paulo ainda é o dia 28 (a cicatriz da data da
 * colação, 22/09).
 */
export function diaBr(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}
function diaCurto(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}`;
}
function ultimoDiaDoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

export function rotuloDoPeriodo(p: Periodo): string {
  if (p.chave === "hoje") return `Hoje, ${diaBr(p.de)}`;
  if (p.chave === "tudo") return `Todas as avaliações, desde ${diaBr(p.de)}`;
  if (p.chave === "personalizado") return p.de === p.ate ? `Em ${diaBr(p.de)}` : `De ${diaBr(p.de)} a ${diaBr(p.ate)}`;
  return `Últimos ${p.chave} dias — ${diaBr(p.de)} a ${diaBr(p.ate)}`;
}

/** Com o que o período é comparado; nulo em "Todos" e quando o hub ainda não tem avaliação do período. */
export function rotuloDoAnterior(p: Periodo): string | null {
  if (!p.anterior) return null;
  const { de, ate } = p.anterior;
  if (de === ate) return p.chave === "hoje" ? `ontem, ${diaBr(de)}` : diaBr(de);
  return `${diaBr(de)} a ${diaBr(ate)}`;
}

/**
 * O que os gráficos e os números dizem no período em que o hub ainda não tem
 * avaliação nenhuma (a comparação 'sem_dado_ainda', 000334): "nenhuma avaliação
 * no período" afirmaria que ninguém avaliou, e o hub só não sabe ainda.
 */
export const AINDA_SEM_AVALIACAO = "O hub ainda não tem avaliação deste período.";

/**
 * A frase de um bloco VAZIO, pela comparação do período (000334): em
 * 'sem_dado_ainda' a ausência é do HUB, e não dos alunos, e a frase diz isso
 * (`ainda`, por padrão a de `AINDA_SEM_AVALIACAO`); nos outros, a do bloco
 * (`comum`). Um lugar só para a condição: ela estava escrita em cada bloco, e a
 * revisão de 02/10 achou sete que tinham ficado com «nenhuma avaliação».
 */
export function vazioDoPeriodo(p: Periodo | null | undefined, comum: string, ainda: string = AINDA_SEM_AVALIACAO): string {
  return p?.comparacao === "sem_dado_ainda" ? ainda : comum;
}

/**
 * A segunda metade da linha do período: com o que ele é comparado e ATÉ
 * QUANDO (000334). Nas mesmas horas, o corte é dito uma vez para os dois lados
 * quando ele cai no último dia de cada um — o caso de todo período que termina
 * hoje —, e com os dois dias quando não cai (o horizonte ficou num dia
 * anterior, porque a importação não trouxe nada depois). Quem decide o corte é
 * o banco; aqui ele só é escrito.
 */
export function comparacaoEmFrase(p: Periodo): string {
  if (p.comparacao === "sem_dado_ainda") {
    if (!p.horizonte) return "o hub ainda não tem avaliação deste período";
    const q = quandoLegivel(p.horizonte);
    return `o hub ainda não tem avaliação deste período (a mais recente é de ${q.dia}, ${horaComArtigo(q.hora)})`;
  }
  const anterior = rotuloDoAnterior(p);
  if (!anterior) return "sem período anterior para comparar";
  if (p.comparacao !== "mesmas_horas" || !p.horizonte || !p.anterior?.comparado_ate) return `comparado com ${anterior}`;
  const h = quandoLegivel(p.horizonte);
  const a = quandoLegivel(p.anterior.comparado_ate);
  if (h.dia === diaBr(p.ate) && a.dia === diaBr(p.anterior.ate)) {
    return `comparado com ${anterior}, os dois até ${horaComArtigo(h.hora)}${p.dias > 1 ? " do último dia" : ""}`;
  }
  return `comparado com ${anterior} — o período até ${h.dia.slice(0, 5)}, ${horaComArtigo(h.hora)}, e o anterior até ${a.dia.slice(0, 5)}, ${horaComArtigo(a.hora)}`;
}

/**
 * O estado de um balde contra o horizonte (000334), como o banco o devolve:
 * 'completo'; 'parcial', o que contém o horizonte (o dia de hoje, a hora de
 * agora); 'a_chegar', o que começa depois dele — com avaliações e médias NULAS,
 * e não zero: o hub só não sabe ainda. Ausente = completo (a leitura de antes
 * da 000334).
 */
export type EstadoDoBalde = "completo" | "parcial" | "a_chegar";

/** Um balde do eixo do tempo, como o banco o devolve. `chave` identifica; `de` e `ate` já vêm recortados pelo período. */
export type Balde = { chave: string; de: string; ate: string; hora: number | null; estado?: EstadoDoBalde };

/**
 * O que a dica e a tabela dizem de um balde que não está completo: "parcial,
 * até às 16:50" (na hora e no dia; na semana e no mês, com o dia: "até 30/09,
 * às 16:50") e "ainda não chegou". Nulo no completo.
 */
export function rotuloDoEstadoDoBalde(b: Balde, g: Granularidade, horizonte: string | null | undefined): string | null {
  if (b.estado === "a_chegar") return "ainda não chegou";
  if (b.estado !== "parcial") return null;
  return horizonte ? `parcial, ${ateOHorizonte(horizonte, g)}` : "parcial";
}

/**
 * "até às 16:50" — no balde de hora ou de dia, que é o dia do horizonte —, e
 * "até 30/09, às 16:50" na semana e no mês, que têm vários dias. Com `comDia`,
 * a data vai sempre: a nota debaixo do gráfico não está ao lado do balde, e
 * "até às 16:50" sozinho se leria como hoje (a revisão de 02/10).
 */
export function ateOHorizonte(horizonte: string, g: Granularidade, comDia = g === "semana" || g === "mes"): string {
  const q = quandoLegivel(horizonte);
  return comDia ? `até ${q.dia.slice(0, 5)}, ${horaComArtigo(q.hora)}` : `até ${horaComArtigo(q.hora)}`;
}

/** O rótulo do EIXO: curto, porque são muitos. */
export function rotuloCurtoDoBalde(b: Balde, g: Granularidade): string {
  if (g === "hora") return `${String(b.hora ?? 0).padStart(2, "0")}h`;
  if (g === "mes") {
    const [a, m] = b.chave.split("-");
    return `${MES_CURTO[Number(m) - 1] ?? m}/${a.slice(2)}`;
  }
  // Na semana, a chave é a SEGUNDA-FEIRA: o eixo fica com passos iguais mesmo
  // quando a primeira semana foi recortada pelo começo do período.
  return diaCurto(g === "semana" ? b.chave : b.de);
}

/** O rótulo da DICA e da tabela: diz exatamente quais dias o ponto cobre. */
export function rotuloLongoDoBalde(b: Balde, g: Granularidade): string {
  if (g === "hora") {
    const h = b.hora ?? 0;
    return `${diaBr(b.de)}, ${faixaDaHora(h)}`;
  }
  if (g === "dia") {
    const [a, m, d] = b.de.split("-").map(Number);
    return `${DIA_DA_SEMANA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()]}, ${diaBr(b.de)}`;
  }
  if (g === "semana") return b.de === b.ate ? diaBr(b.de) : `de ${diaCurto(b.de)} a ${diaBr(b.ate)}`;
  const [a, m] = b.chave.split("-").map(Number);
  const inteiro = b.de.endsWith("-01") && Number(b.ate.slice(8, 10)) === ultimoDiaDoMes(a, m);
  return `${MES_LONGO[m - 1] ?? m} de ${a}${inteiro ? "" : ` (de ${diaCurto(b.de)} a ${diaCurto(b.ate)})`}`;
}

// ── As faixas (para PINTAR; quem CONTA é o banco) ───────────────────────────

/**
 * As faixas da média de uma avaliação (0 a 10), as mesmas do painel antigo:
 * satisfeita é média 8 ou mais (o "CSAT" de lá), baixa é média 5 ou menos (os
 * "detratores" de lá); promotor é média 9 ou mais e detrator, abaixo de 7 — o
 * NPS ADAPTADO, que não é o NPS das pesquisas (a nota de 0 a 10 do
 * "recomendaria?"). Quem CONTA é o banco; aqui elas só decidem a cor de uma
 * média que a tela já mostra. O teste 111 prova as fronteiras no banco, e a
 * prova de tela confere que o filtro "baixas" do banco só traz selo de baixa.
 */
export const FAIXAS = { satisfeitaDesde: 8, baixaAte: 5, promotorDesde: 9, detratorAbaixoDe: 7 } as const;

export type FaixaDaMedia = "satisfeita" | "intermediaria" | "baixa";

export function faixaDaMedia(m: number): FaixaDaMedia {
  return m >= FAIXAS.satisfeitaDesde ? "satisfeita" : m <= FAIXAS.baixaAte ? "baixa" : "intermediaria";
}

// ── Os números ──────────────────────────────────────────────────────────────

/**
 * Os números de um período, só com as avaliações de ALUNOS (a equipe fica
 * fora; `equipe_fora` diz quantas ficaram). Nulo quando não há avaliação: nulo
 * não é zero. Os percentuais têm uma casa e a média, duas.
 */
export type Numeros = {
  avaliacoes: number;
  media_geral: number | null;
  satisfeitas: number;
  satisfeitas_pct: number | null;
  baixas: number;
  baixas_pct: number | null;
  promotores: number;
  neutros: number;
  detratores: number;
  /**
   * A fatia de cada categoria do NPS adaptado, em %, com uma casa — a mesma
   * conta dos outros percentuais (`round(100.0 * x / n, 1)`), feita no banco
   * desde 29/09. Antes a Visão geral a refazia no navegador, e era a segunda
   * definição de um percentual que o banco já sabia fazer. Nulos sem avaliação.
   *
   * Podem vir AUSENTES na janela em que o banco ainda responde com a leitura da
   * 000328, que não os trazia: a tela os lê como nulos e escreve "—", e não
   * refaz a conta.
   */
  promotores_pct: number | null;
  neutros_pct: number | null;
  detratores_pct: number | null;
  /** % de promotores menos % de detratores, inteiro de −100 a 100. */
  nps_adaptado: number | null;
  ferramentas_avaliadas: number;
  equipe_fora: number;
};

/**
 * A variação contra o período anterior, cada uma na SUA unidade, e já feita
 * pelo banco: contagens em % (`avaliacoes_pct`), a média em pontos (sobre as
 * duas médias arredondadas, as que a tela mostra), os percentuais em pontos
 * percentuais e o NPS adaptado em pontos. Nula quando não há com o que
 * comparar (Todos, ou o período anterior sem avaliação).
 */
export type Variacao = {
  avaliacoes_pct: number | null;
  media_geral: number | null;
  satisfeitas_pct: number | null;
  baixas_pct: number | null;
  nps_adaptado: number | null;
  ferramentas_avaliadas_pct: number | null;
};

/** Um ponto da série de TODAS as ferramentas (a Visão geral). As avaliações são nulas no balde que ainda não chegou (000334). */
export type PontoDaSerie = Balde & { avaliacoes: number | null; media_geral: number | null };

export type SituacaoDaMeta = "atingida" | "abaixo" | "sem_meta" | "sem_avaliacao";

/**
 * Uma ferramenta num período. A MESMA linha nas três leituras que a trazem —
 * a Visão geral, as Ferramentas e o Comparar —, porque é UMA função do banco
 * que a monta.
 */
export type LinhaDaFerramenta = {
  slug: string;
  nome: string;
  destino: string;
  meta: number | null;
  /** A média ARREDONDADA (a que a tela mostra) contra a meta — decidido no banco. */
  situacao_meta: SituacaoDaMeta;
  avaliacoes: number;
  /** A fatia desta ferramenta nas avaliações do período, em %. */
  participacao_pct: number | null;
  media_geral: number | null;
  utilidade: number | null;
  facilidade: number | null;
  satisfacao: number | null;
  satisfeitas: number;
  satisfeitas_pct: number | null;
  baixas: number;
  baixas_pct: number | null;
  promotores: number;
  neutros: number;
  detratores: number;
  nps_adaptado: number | null;
  com_comentario: number;
  hub: number;
  central_feedbacks: number;
  equipe: number;
  ultima: string | null;
  /** Nulas quando o período não tem anterior (Todos). */
  anterior_avaliacoes: number | null;
  anterior_media_geral: number | null;
  variacao_media: number | null;
};

/** O nome antigo (000327): as peças que já o usavam continuam valendo. */
export type LinhaDaComparacao = LinhaDaFerramenta;

export type MotivoDeAtencao = {
  /** `valor` é, na ordem: os pontos que a média caiu, a média, quantas baixas, a meta. */
  tipo: "queda" | "media_baixa" | "baixas" | "abaixo_da_meta";
  valor: number;
};

/**
 * As regras são as do painel antigo — a média caiu 1 ponto ou mais contra o
 * período anterior, a média está abaixo de 6, ou há 3 ou mais avaliações
 * baixas — e mais uma que só existe aqui: abaixo da META que o time definiu.
 * Medido em 29/09, nos 30 dias: sem essa, a ferramenta de pior média (o Radar
 * Simples, 8,75 contra a meta 9,0) não aparecia na lista.
 */
export type FerramentaEmAtencao = {
  slug: string;
  nome: string;
  avaliacoes: number;
  media_geral: number;
  variacao_media: number | null;
  baixas: number;
  meta: number | null;
  motivos: MotivoDeAtencao[];
};

export type Origem = "hub" | "central_feedbacks";

/** Uma avaliação, como a equipe a vê: nota e comentário, nunca quem escreveu. */
export type AvaliacaoDaLista = {
  seq: number;
  /** Quando a avaliação foi dada — ou mudada pela última vez. É por ela que o período filtra. */
  em: string;
  card: string;
  nome: string;
  origem: Origem;
  utilidade: number;
  facilidade: number;
  satisfacao: number;
  /** A média das três notas, com duas casas. */
  media: number;
  comentario: string | null;
};

/**
 * O retrato da importação do app antigo (a Central Feedbacks): quantas vieram,
 * quando a última importação rodou e até quando ela trouxe. É de TODAS as
 * ferramentas, e não do período — é ele que diz até quando o hub sabe o que o
 * app antigo recebeu. O banco o monta num lugar só (`av_importacao`), e a Visão
 * geral e a comparação o devolvem igual.
 */
export type Importacao = { importadas: number; ultima_importacao: string | null; mais_recente: string | null };

export type VisaoGeral = {
  ok: true;
  periodo: Periodo;
  /**
   * Desde 29/09. Pode vir AUSENTE na janela em que o banco ainda responde com a
   * leitura da 000328, que não o trazia — e ausente é "não sei", não "nenhuma
   * importação" (ver `fraseDaImportacao`).
   */
  importacao: Importacao;
  indicadores: { atual: Numeros; anterior: Numeros | null; variacao: Variacao };
  /** Um ponto por balde do período, inclusive os vazios: o eixo é o TEMPO. */
  serie: PontoDaSerie[];
  /** Só as que têm avaliação no período, da mais avaliada para a menos. */
  ferramentas: LinhaDaFerramenta[];
  /** Todas as que cruzam uma regra, da pior média para a melhor. */
  atencao: FerramentaEmAtencao[];
  /** As 10 mais recentes de média 5 ou menos COM comentário. */
  baixas_recentes: AvaliacaoDaLista[];
};

export type Comparacao = {
  ok: true;
  periodo: Periodo;
  resumo: {
    ferramentas: number;
    com_avaliacao: number;
    avaliacoes: number;
    media_geral: number | null;
    com_meta: number;
    atingiram: number;
    abaixo_da_meta: number;
  };
  /** O retrato da importação do app antigo — de todas as ferramentas, e não do período. */
  importacao: Importacao;
  /** As ferramentas que moram no hub e toda ferramenta que já recebeu avaliação — com ou sem avaliação NO período. */
  ferramentas: LinhaDaFerramenta[];
};

export type OrdemDaLista = "recentes" | "antigas" | "menor_media" | "maior_media";

export type FiltrosDaLista = {
  card: string | null;
  /** No comentário ou no nome da ferramenta, sem distinguir acento nem caixa. */
  busca: string;
  mediaMin: number | null;
  mediaMax: number | null;
  /** `true` só com comentário, `false` só sem, `null` as duas. */
  comentario: boolean | null;
  ordem: OrdemDaLista;
  limite: number;
  offset: number;
};

export type ListaDeAvaliacoes = {
  ok: true;
  periodo: Periodo;
  /** As avaliações de alunos no período, sem os outros filtros. */
  total_no_periodo: number;
  /** As que passam por todos os filtros. */
  total: number;
  limite: number;
  offset: number;
  itens: AvaliacaoDaLista[];
  /** Para o seletor: as ferramentas com avaliação no período (e a escolhida, mesmo sem nenhuma). */
  ferramentas: { slug: string; nome: string; avaliacoes: number }[];
};

export type PontoDaFerramenta = {
  chave: string;
  /** Nula no balde que ainda não chegou (000334), como as médias. */
  avaliacoes: number | null;
  estado?: EstadoDoBalde;
  media_geral: number | null;
  utilidade: number | null;
  facilidade: number | null;
  satisfacao: number | null;
};

/** A série de UMA ferramenta: `pontos` tem o tamanho e a ordem de `baldes`. */
export type SerieDaFerramenta = { card: string; nome: string; meta: number | null; pontos: PontoDaFerramenta[] };

export type Series = { ok: true; periodo: Periodo; baldes: Balde[]; series: SerieDaFerramenta[] };

// ── As leituras ─────────────────────────────────────────────────────────────

/**
 * Os códigos de erro de uma leitura que NÃO EXISTE no banco: `PGRST202` é o do
 * PostgREST, quando a função (com estes parâmetros) não está no cache dele, e
 * `42883` é o do Postgres, quando ela existe e chama uma FUNÇÃO que não existe.
 * Os dois querem dizer que a tela e o banco não estão na mesma versão.
 *
 * O `42883` só conta com a mensagem começando por "function" (a revisão da
 * tela de 30/09): o Postgres usa o mesmo código para "operator does not exist",
 * que é um defeito de TIPO dentro de uma função que existe, e aí a frase de
 * versão diria que a leitura não existe, o que é falso. Esse fica na falha
 * comum.
 */
function leituraQueNaoExiste(error: { code?: string; message?: string }): boolean {
  if (error.code === "PGRST202") return true;
  return error.code === "42883" && /^function\b/i.test(error.message ?? "");
}

/**
 * `naoVeio` é o nome da leitura dito como a tela o diz, com o verbo
 * ("a visão geral não veio", "as avaliações não vieram"): a frase de falha é
 * montada a partir dele.
 *
 * A falha comum (a rede, o servidor fora) pede recarregar. A leitura que não
 * existe no banco pede OUTRA coisa, e a frase tem de dizer: em 29/09 Victor viu
 * as quatro sub-abas dizendo "tente recarregar a página" com a 000328 ainda
 * sem aplicar — a tela tinha subido antes do banco —, e recarregar não resolvia
 * nada. Quem resolve é quem mantém o hub, aplicando a migration.
 *
 * A frase NÃO diz qual dos dois está à frente (a revisão da tela de 30/09): o
 * mesmo erro sai quando a tela é mais VELHA que o banco — uma aba aberta com o
 * pacote antigo depois de uma migration que troca uma assinatura —, e aí
 * recarregar a página resolve. Por isso ela pede para recarregar primeiro e só
 * depois para avisar.
 */
async function chamar<T>(rpc: string, args: Record<string, unknown>, naoVeio: string, sinal?: AbortSignal): Promise<T | Falha> {
  let q = supabase.rpc(rpc, args);
  if (sinal) q = q.abortSignal(sinal);
  const { data, error } = await q;
  if (error) {
    return {
      ok: false,
      detalhe: leituraQueNaoExiste(error)
        ? `${naoVeio} — a tela e o banco não estão na mesma versão, e a leitura que ela pede não existe lá. Recarregue a página; se continuar, avise quem mantém o hub.`
        : `${naoVeio} — tente recarregar a página.`,
    };
  }
  const r = data as T | Falha | null;
  if (!r) return { ok: false, detalhe: "o servidor não respondeu." };
  return r;
}

export function lerVisaoGeral(p: PedidoDePeriodo, sinal?: AbortSignal): Promise<VisaoGeral | Falha> {
  return chamar<VisaoGeral>("av_admin_visao_geral", parametrosDoPeriodo(p), "a visão geral não veio", sinal);
}

export function lerComparacao(p: PedidoDePeriodo, sinal?: AbortSignal): Promise<Comparacao | Falha> {
  return chamar<Comparacao>("av_admin_comparacao", parametrosDoPeriodo(p), "as ferramentas não vieram", sinal);
}

export function lerAvaliacoes(p: PedidoDePeriodo, f: FiltrosDaLista, sinal?: AbortSignal): Promise<ListaDeAvaliacoes | Falha> {
  return chamar<ListaDeAvaliacoes>(
    "av_admin_avaliacoes",
    {
      ...parametrosDoPeriodo(p),
      p_card: f.card,
      p_busca: f.busca.trim() || null,
      p_media_min: f.mediaMin,
      p_media_max: f.mediaMax,
      p_comentario: f.comentario,
      p_ordem: f.ordem,
      p_limite: f.limite,
      p_offset: f.offset,
    },
    "as avaliações não vieram",
    sinal,
  );
}

/** De uma a quatro ferramentas, na ordem pedida (a ordem é a da cor). */
export function lerSeries(cards: string[], p: PedidoDePeriodo, sinal?: AbortSignal): Promise<Series | Falha> {
  return chamar<Series>("av_admin_series", { p_cards: cards, ...parametrosDoPeriodo(p) }, "a evolução não veio", sinal);
}

/**
 * O estado de uma leitura do painel, com a regra de SEMPRE: só vale a resposta
 * do último pedido (trocar o período duas vezes seguidas faria a primeira
 * resposta chegar depois e desenhar o período errado — a cicatriz dos filtros
 * das sugestões, 000321), e a releitura MANTÉM o quadro, marcado como
 * atualizando, em vez de trocá-lo por um carregador (sem pulo de layout).
 *
 * `chave` é o que muda a leitura: o período e os filtros, numa string. A
 * função `ler` pode mudar a cada render; quem dispara é a chave.
 */
export type EstadoDaLeitura<T> =
  | { fase: "carregando" }
  | { fase: "negado"; detalhe: string }
  | { fase: "erro"; detalhe: string }
  | { fase: "pronto"; dados: T; atualizando: boolean };

/**
 * O estado é DERIVADO no render, a partir do pedido que trouxe a última
 * resposta — e não escrito por um efeito quando a chave muda. A primeira versão
 * (000328) marcava "atualizando" num efeito, que roda DEPOIS do render: no
 * quadro em que a chave nova chegava, a tela desenhava os dados velhos marcados
 * como PRONTOS, e duas telas tinham remendos para isso (a chave do pedido das
 * séries conferida de novo no gráfico do Comparar, o deslocamento conferido na
 * página de uma ferramenta). Derivado, o mesmo render que recebe a chave nova já
 * sabe que os dados são de outro pedido:
 *
 *   · resposta boa de OUTRO pedido → o quadro fica, marcado como atualizando;
 *   · recusa ou falha de outro pedido → carregando (a frase de uma falha não
 *     vale para um pedido que ainda nem foi respondido);
 *   · qualquer resposta do pedido de AGORA → ela, como veio.
 *
 * O pedido é a chave com a VERSÃO: `recarregar` pede de novo a mesma chave, e a
 * resposta antiga passa a ser "de outro pedido" até a nova chegar.
 */
export function useLeitura<T extends { ok: true }>(
  ler: (sinal: AbortSignal) => Promise<T | Falha>,
  chave: string,
): { estado: EstadoDaLeitura<T>; recarregar: () => void } {
  const [versao, setVersao] = useState(0);
  const [ultima, setUltima] = useState<{ pedido: string; resposta: T | Falha } | null>(null);
  const lerAtual = useRef(ler);
  useLayoutEffect(() => {
    lerAtual.current = ler;
  });
  // A versão vem primeiro e é um número: ela não tem ":" dentro, então dois
  // pares (versão, chave) diferentes nunca dão o mesmo pedido.
  const pedido = `${versao}:${chave}`;
  useEffect(() => {
    const controle = new AbortController();
    let vivo = true;
    void lerAtual.current(controle.signal).then((resposta) => {
      if (vivo) setUltima({ pedido, resposta });
    });
    return () => {
      vivo = false;
      controle.abort();
    };
  }, [pedido]);
  // Memorizado: quem depende do estado num efeito (a reserva da página de uma
  // ferramenta) não roda a cada render por causa de um objeto novo com o mesmo
  // conteúdo.
  const estado = useMemo<EstadoDaLeitura<T>>(() => {
    if (!ultima) return { fase: "carregando" };
    const { resposta } = ultima;
    const doPedidoDeAgora = ultima.pedido === pedido;
    if (resposta.ok) return { fase: "pronto", dados: resposta, atualizando: !doPedidoDeAgora };
    if (!doPedidoDeAgora) return { fase: "carregando" };
    return resposta.motivo === "sem_permissao"
      ? { fase: "negado", detalhe: resposta.detalhe }
      : { fase: "erro", detalhe: resposta.detalhe };
  }, [ultima, pedido]);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  return { estado, recarregar };
}

// ── A meta (000327) ─────────────────────────────────────────────────────────

export type RespostaDaMeta = {
  ok: boolean;
  mudou?: boolean;
  meta?: number | null;
  motivo?: string;
  detalhe?: string;
};

/** Define (ou tira, com `null`) a meta. Quem valida a escala e a casa decimal é o banco. */
export async function definirMeta(card: string, meta: number | null, motivo: string): Promise<RespostaDaMeta> {
  const { data, error } = await supabase.rpc("av_admin_definir_meta", {
    p_card: card,
    p_meta: meta,
    p_motivo: motivo.trim() || null,
  });
  if (error) return { ok: false, detalhe: "não foi possível falar com o servidor — a meta não mudou." };
  return (data as RespostaDaMeta) ?? { ok: false, detalhe: "o servidor não respondeu." };
}

/**
 * O texto do campo virando número, na FRONTEIRA com o servidor: aceita "8,5" e
 * "8.5". O que não é número volta nulo; a escala (0 a 10) e a casa decimal quem
 * confere é o banco, com a frase dele.
 */
export function metaDoTexto(texto: string): number | null {
  const t = texto.trim().replace(",", ".");
  if (!/^\d{1,2}(\.\d+)?$/.test(t)) return null;
  return Number(t);
}

// ── O formato dos números ───────────────────────────────────────────────────

/** A nota como o público brasileiro lê: duas casas, vírgula. Nula vira "—". */
export function notaLegivel(n: number | null | undefined): string {
  return n === null || n === undefined
    ? "—"
    : n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A média de UMA avaliação, com uma casa: a soma de três notas inteiras
 * dividida por 3 só termina em ,0 ,3 ou ,7 — a segunda casa não diz nada.
 */
export function notaCurta(n: number | null | undefined): string {
  return n === null || n === undefined
    ? "—"
    : n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** A meta tem uma casa só: 9,0, e não 9,00. */
export function metaLegivel(n: number | null | undefined): string {
  return n === null || n === undefined
    ? "—"
    : n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Percentual com uma casa: "93,5%". */
export function pctLegivel(n: number | null | undefined): string {
  return n === null || n === undefined
    ? "—"
    : `${n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/**
 * A variação, com sinal e na unidade de cada número. O menos é o sinal de
 * menos de verdade (−), e não o hífen: o leitor de tela o lê como "menos".
 *
 * As unidades: `pct` é a variação percentual de uma contagem ("+12,5%");
 * `pontos`, a da média ("−0,49"); `pp`, a de um percentual, em pontos
 * percentuais; `inteiro`, a do NPS adaptado ("+5").
 */
export type UnidadeDaVariacao = "pct" | "pontos" | "pp" | "inteiro";

/** Só o número da variação, com o sinal e as casas da unidade — sem a unidade. */
export function numeroDaVariacao(v: number, unidade: UnidadeDaVariacao): string {
  const casas = unidade === "pontos" ? 2 : unidade === "inteiro" ? 0 : 1;
  const modulo = Math.abs(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
  const sinal = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sinal}${modulo}`;
}

/**
 * A unidade por EXTENSO, quando ela é palavra: "ponto percentual" e "pontos
 * percentuais", com o plural a partir do 2 — a regra do motivo de queda da
 * Visão geral ("1,20 ponto", "2,40 pontos"), como em "1,5 milhão". Era "p.p." até
 * 29/09: abreviação na tela, que a casa não usa e que o leitor de tela soletra.
 * Vazia nas outras unidades (o "%" vai colado ao número, em `variacaoLegivel`).
 */
export function unidadeDaVariacao(v: number, unidade: UnidadeDaVariacao): string {
  if (unidade !== "pp") return "";
  return Math.abs(v) < 2 ? "ponto percentual" : "pontos percentuais";
}

export function variacaoLegivel(v: number | null | undefined, unidade: UnidadeDaVariacao): string {
  if (v === null || v === undefined) return "—";
  const numero = numeroDaVariacao(v, unidade);
  if (unidade === "pct") return `${numero}%`;
  const palavras = unidadeDaVariacao(v, unidade);
  return palavras ? `${numero} ${palavras}` : numero;
}

/** Se a variação é boa, ruim ou nenhuma, dado o sentido do número. Apresentação: quem calculou a variação foi o banco. */
export function sentidoDaVariacao(v: number | null | undefined, maiorEMelhor: boolean): "melhor" | "pior" | "igual" | "sem" {
  if (v === null || v === undefined) return "sem";
  if (v === 0) return "igual";
  return v > 0 === maiorEMelhor ? "melhor" : "pior";
}

export const ROTULO_DA_ORIGEM: Record<Origem, string> = {
  hub: "hub",
  central_feedbacks: "app antigo",
};

export const ROTULO_DA_SITUACAO: Record<SituacaoDaMeta, string> = {
  atingida: "atingida",
  abaixo: "abaixo da meta",
  sem_meta: "sem meta",
  sem_avaliacao: "sem avaliação",
};

/** A data e a hora de uma avaliação, no fuso de São Paulo — o dia que o período usa. */
export function quandoLegivel(iso: string): { dia: string; hora: string } {
  const d = new Date(iso);
  return {
    dia: d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }),
    hora: d.toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" }),
  };
}

// ── As planilhas ────────────────────────────────────────────────────────────

/** Páginas de mil, como a planilha das matrículas; e um teto DITO, nunca silencioso. */
export const LOTE_DA_PLANILHA = 1000;
export const TETO_DA_PLANILHA = 20000;

/**
 * A planilha da lista, com os MESMOS filtros da tela, paginando pela MESMA
 * função: reescrever o filtro aqui criaria a segunda definição de "o que está
 * sendo exportado" (a razão da planilha das matrículas, 000206). Sem e-mail e
 * sem aluno, porque o hub não os guarda.
 */
export async function planilhaDaLista(
  p: PedidoDePeriodo,
  f: Omit<FiltrosDaLista, "limite" | "offset">,
  aoAndar?: (lidas: number, total: number) => void,
): Promise<{ ok: true; periodo: Periodo; linhas: number; total: number; truncado: boolean } | Falha> {
  const itens: AvaliacaoDaLista[] = [];
  let total = 0;
  let periodo: Periodo | null = null;
  for (;;) {
    const r = await lerAvaliacoes(p, { ...f, limite: LOTE_DA_PLANILHA, offset: itens.length });
    if (!r.ok) return r;
    periodo = r.periodo;
    total = r.total;
    itens.push(...r.itens);
    aoAndar?.(itens.length, Math.min(total, TETO_DA_PLANILHA));
    if (r.itens.length === 0 || itens.length >= total || itens.length >= TETO_DA_PLANILHA) break;
  }
  const corpo = itens.slice(0, TETO_DA_PLANILHA).map((a) => {
    const q = quandoLegivel(a.em);
    return [
      a.nome,
      q.dia,
      q.hora,
      a.utilidade,
      a.facilidade,
      a.satisfacao,
      numeroParaCsv(a.media, 2),
      a.comentario ?? "",
      a.origem === "hub" ? "hub" : "app antigo (Central Feedbacks)",
    ];
  });
  const csv = montarCsv(
    ["Ferramenta", "Dia", "Hora", "Utilidade", "Facilidade", "Satisfação", "Média", "Comentário", "Origem"],
    corpo,
  );
  if (!periodo) return { ok: false, detalhe: "o servidor não respondeu." };
  baixarCsv(csv, `avaliacoes-${periodo.de}-a-${periodo.ate}.csv`);
  return { ok: true, periodo, linhas: corpo.length, total, truncado: total > corpo.length };
}

/**
 * O relatório por ferramenta do período da Visão geral — o "Exportar CSV" do
 * painel antigo, que lá era por MÊS. Aqui ele é do período escolhido (um mês é
 * o personalizado do primeiro ao último dia), e a comparação é com o período
 * anterior de mesma duração: o mesmo que a tela mostra.
 */
export function planilhaDasFerramentas(v: VisaoGeral): void {
  const rotulo = rotuloDoPeriodo(v.periodo);
  // Com o QUE e até QUANDO (000334): nas mesmas horas, o anterior foi cortado, e
  // quem abre a planilha leria o total dele como o do período inteiro.
  const comparacao = comparacaoEmFrase(v.periodo);
  const comparacaoEscrita = comparacao.charAt(0).toLocaleUpperCase("pt-BR") + comparacao.slice(1);
  const corpo = v.ferramentas.map((l) => [
    rotulo,
    l.nome,
    l.avaliacoes,
    numeroParaCsv(l.participacao_pct, 1),
    numeroParaCsv(l.media_geral, 2),
    numeroParaCsv(l.utilidade, 2),
    numeroParaCsv(l.facilidade, 2),
    numeroParaCsv(l.satisfacao, 2),
    numeroParaCsv(l.satisfeitas_pct, 1),
    l.baixas,
    numeroParaCsv(l.baixas_pct, 1),
    l.nps_adaptado ?? "",
    numeroParaCsv(l.meta, 1),
    ROTULO_DA_SITUACAO[l.situacao_meta],
    l.anterior_avaliacoes ?? "",
    numeroParaCsv(l.anterior_media_geral, 2),
    numeroParaCsv(l.variacao_media, 2),
    comparacaoEscrita,
  ]);
  const csv = montarCsv(
    [
      "Período",
      "Ferramenta",
      "Avaliações",
      "Participação (%)",
      "Média geral",
      "Utilidade",
      "Facilidade",
      "Satisfação",
      "Satisfeitas (%)",
      "Baixas",
      "Baixas (%)",
      "NPS adaptado",
      "Meta",
      "Situação da meta",
      "Avaliações no período anterior",
      "Média no período anterior",
      "Variação da média",
      "Comparação com o anterior",
    ],
    corpo,
  );
  baixarCsv(csv, `avaliacoes-por-ferramenta-${v.periodo.de}-a-${v.periodo.ate}.csv`);
}
